import { useState } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  AddOutlined,
  HistoryOutlined,
  FolderOutlined,
  SendOutlined,
} from '@mui/icons-material';
import { StyledDataGridWrapper } from '@/components/StyledDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  ContentTypeSelector,
  AspectRatioSelector,
  FileUpload,
  AssetCard,
  ContentType,
  TopicCell,
  TypeCell,
  StatusCell,
  NameCell,
  ActionsCell,
  getContentTypeIcon,
} from '@/features/studio';
import {
  PageHeader,
  ContentCard,
  TabPanel,
  RequiredLabel,
  SplitLayout,
  TabContentWrapper,
  EmptyState,
} from '@/components/common';

interface Agent {
  id: string;
  name: string;
  role: string;
  is_default: boolean;
}

interface Conversation {
  id: number;
  topic: string;
  angle: string;
  created_at: string;
  status: string;
}

interface Asset {
  id: number;
  name: string;
  type: string;
  created_at: string;
  size: string;
}

function StudioPage() {
  const [activeTab, setActiveTab] = useState(0);

  // Form states
  const [contentType, setContentType] = useState<ContentType>('article');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [topic, setTopic] = useState('');
  const [angle, setAngle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [videoAspectRatio, setVideoAspectRatio] = useState('9:16');
  const [videoDuration, setVideoDuration] = useState(10);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [agents] = useState<Agent[]>([
    { id: '1', name: 'Content Writer Pro', role: 'content-writer', is_default: true },
    { id: '2', name: 'Image Creator', role: 'image-creator', is_default: false },
    { id: '3', name: 'Video Editor', role: 'video-editor', is_default: false },
  ]);

  const [conversations] = useState<Conversation[]>([
    {
      id: 1,
      topic: 'Best Practices for Remote Work',
      angle: 'article',
      created_at: new Date().toISOString(),
      status: 'completed',
    },
    {
      id: 2,
      topic: 'Product Photography',
      angle: 'image',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      status: 'completed',
    },
    {
      id: 3,
      topic: 'Marketing Video',
      angle: 'video',
      created_at: new Date(Date.now() - 172800000).toISOString(),
      status: 'in_progress',
    },
  ]);

  const [assets] = useState<Asset[]>([
    {
      id: 1,
      name: 'Remote Work Guide.md',
      type: 'article',
      created_at: new Date().toISOString(),
      size: '15 KB',
    },
    {
      id: 2,
      name: 'product-shot-001.jpg',
      type: 'image',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      size: '2.4 MB',
    },
    {
      id: 3,
      name: 'promo-video.mp4',
      type: 'video',
      created_at: new Date(Date.now() - 172800000).toISOString(),
      size: '45.2 MB',
    },
  ]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async () => {
    // Validation
    if ((contentType === 'article' || contentType === 'image') && !topic.trim()) {
      setError('Topic is required');
      return;
    }
    if (contentType === 'video' && !instructions.trim()) {
      setError('Instructions are required for video generation');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // TODO: API call to create conversation
      console.log('Creating conversation:', {
        contentType,
        topic,
        angle,
        instructions,
        aspectRatio: contentType === 'image' ? aspectRatio : videoAspectRatio,
        videoDuration: contentType === 'video' ? videoDuration : null,
        selectedAgent,
        file: attachedFile,
      });

      // Reset form after successful submission
      setTopic('');
      setAngle('');
      setInstructions('');
      setAttachedFile(null);

      // Switch to history tab to show the new conversation
      setActiveTab(1);
    } catch (err) {
      setError('Failed to create conversation. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const aspectRatios = {
    image: [
      { ratio: '1:1', desc: 'Square', width: 28, height: 28 },
      { ratio: '16:9', desc: 'Landscape', width: 40, height: 22 },
      { ratio: '4:5', desc: 'Portrait', width: 24, height: 30 },
      { ratio: '9:16', desc: 'Stories', width: 18, height: 32 },
    ],
    video: [
      { ratio: '16:9', desc: 'Landscape', width: 40, height: 22 },
      { ratio: '9:16', desc: 'Portrait', width: 18, height: 32 },
    ],
  };

  const availableAgents = agents.filter((agent) => {
    if (contentType === 'article') return agent.role === 'content-writer';
    if (contentType === 'image') return agent.role === 'image-creator';
    if (contentType === 'video') return agent.role === 'video-editor';
    return false;
  });

  // DataGrid columns for History
  const historyColumns: GridColDef[] = [
    {
      field: 'topic',
      headerName: 'Topic',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <TopicCell topic={params.row.topic} angle={params.row.angle} />
      ),
    },
    {
      field: 'angle',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => <TypeCell type={params.row.angle} />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      valueFormatter: (value) => formatDate(value as string),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <StatusCell status={params.row.status} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <ActionsCell
          onView={() => console.log('View', params.row.id)}
          onDelete={() => console.log('Delete', params.row.id)}
        />
      ),
    },
  ];

  // DataGrid columns for Assets
  const assetsColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => <NameCell name={params.row.name} />,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => <TypeCell type={params.row.type} />,
    },
    {
      field: 'size',
      headerName: 'Size',
      width: 120,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      valueFormatter: (value) => formatDate(value as string),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <ActionsCell
          onView={() => console.log('View', params.row.id)}
          onDelete={() => console.log('Delete', params.row.id)}
        />
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 3 }}>
      <PageHeader
        title="AI Content Studio"
        subtitle="Create articles, images, and videos with AI assistance"
      />

      <SplitLayout
        mainWidth={75}
        gap={3}
        main={
          <ContentCard>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="studio tabs">
                <Tab
                  icon={<AddOutlined />}
                  label="New Content"
                  iconPosition="start"
                  id="studio-tab-0"
                  aria-controls="studio-tabpanel-0"
                />
                <Tab
                  icon={<HistoryOutlined />}
                  label="History"
                  iconPosition="start"
                  id="studio-tab-1"
                  aria-controls="studio-tabpanel-1"
                />
                <Tab
                  icon={<FolderOutlined />}
                  label="All Assets"
                  iconPosition="start"
                  id="studio-tab-2"
                  aria-controls="studio-tabpanel-2"
                />
              </Tabs>
            </Box>

            {/* New Content Tab */}
            <TabPanel value={activeTab} index={0}>
              <TabContentWrapper>
                <Stack>
                  {error && (
                    <Alert severity="error" onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <FormControl fullWidth>
                    <InputLabel id="agent-select-label">Select Agent</InputLabel>
                    <Select
                      labelId="agent-select-label"
                      value={selectedAgent}
                      label="Select Agent"
                      onChange={(e) => setSelectedAgent(e.target.value)}
                    >
                      <MenuItem value="">-- Use Default --</MenuItem>
                      {availableAgents.map((agent) => (
                        <MenuItem key={agent.id} value={agent.id}>
                          {agent.name} {agent.is_default ? '(Default)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <ContentTypeSelector value={contentType} onChange={setContentType} />

                  {(contentType === 'article' || contentType === 'image') && (
                    <>
                      <TextField
                        fullWidth
                        label={<RequiredLabel>Topic</RequiredLabel>}
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder={
                          contentType === 'article'
                            ? 'e.g., Best Practices for Remote Work'
                            : 'e.g., Dental Implants'
                        }
                      />

                      <TextField
                        fullWidth
                        label="Angle (Optional)"
                        value={angle}
                        onChange={(e) => setAngle(e.target.value)}
                        placeholder={
                          contentType === 'article'
                            ? 'e.g., Focus on productivity tools and work-life balance'
                            : 'e.g., Affordability'
                        }
                      />
                    </>
                  )}

                  {contentType === 'video' && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={<RequiredLabel>Instructions</RequiredLabel>}
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="Describe the video you want to create..."
                    />
                  )}

                  {(contentType === 'article' || contentType === 'image') && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Additional Instructions (Optional)"
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="Any specific requirements or preferences..."
                    />
                  )}

                  {contentType === 'image' && (
                    <AspectRatioSelector
                      value={aspectRatio}
                      onChange={setAspectRatio}
                      ratios={aspectRatios.image}
                    />
                  )}

                  {contentType === 'video' && (
                    <>
                      <AspectRatioSelector
                        value={videoAspectRatio}
                        onChange={setVideoAspectRatio}
                        ratios={aspectRatios.video}
                      />

                      <TextField
                        fullWidth
                        type="number"
                        label="Duration (seconds)"
                        value={videoDuration}
                        onChange={(e) => setVideoDuration(Number(e.target.value))}
                        inputProps={{ min: 5, max: 60, step: 5 }}
                      />
                    </>
                  )}

                  <FileUpload file={attachedFile} onChange={setAttachedFile} />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} /> : <SendOutlined />}
                  >
                    {loading ? 'Creating...' : 'Generate Content'}
                  </Button>
                </Stack>
              </TabContentWrapper>
            </TabPanel>

            {/* History Tab */}
            <TabPanel value={activeTab} index={1}>
              <TabContentWrapper withBottomPadding>
                <StyledDataGridWrapper
                  rows={conversations}
                  columns={historyColumns}
                  height={500}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                  }}
                />
              </TabContentWrapper>
            </TabPanel>

            {/* All Assets Tab */}
            <TabPanel value={activeTab} index={2}>
              <TabContentWrapper withBottomPadding>
                <StyledDataGridWrapper
                  rows={assets}
                  columns={assetsColumns}
                  height={500}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                  }}
                />
              </TabContentWrapper>
            </TabPanel>
          </ContentCard>
        }
        sidebar={
          <ContentCard sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Generated Assets
            </Typography>

            {assets.length === 0 ? (
              <EmptyState icon={<FolderOutlined />} message="No assets yet" />
            ) : (
              <Stack spacing={2}>
                {assets.slice(0, 5).map((asset) => (
                  <AssetCard
                    key={asset.id}
                    icon={getContentTypeIcon(asset.type)}
                    name={asset.name}
                    size={asset.size}
                    date={formatDate(asset.created_at)}
                    onClick={() => console.log('Asset clicked:', asset.id)}
                  />
                ))}
                {assets.length > 5 && (
                  <Button variant="text" size="small" onClick={() => setActiveTab(2)}>
                    View All ({assets.length})
                  </Button>
                )}
              </Stack>
            )}
          </ContentCard>
        }
      />
    </Container>
  );
}

export default StudioPage;
